import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import AddressManagementService, { Address, AddressGoogleInfo, AddressRadarInfo, AddressUberInfo, AddressReportResponse, CorrectCoordinatesResponse, CorrectFullAddressResponse, GeminiPreprocessingLogResponseDto, getLatestDeliveredCoordinates, getAllDeliveredCoordinates } from '../../services/addressManagementService';
import PodsService from '../../services/podsService';
import JsonViewer from '../common/JsonViewer';
import AddressHistoryTimeline from './AddressHistoryTimeline';
import AddressNotes from './AddressNotes';
import ComparisonMap from './ComparisonMap';
import CoordinatesComparisonMapModal from './CoordinatesComparisonMapModal';
import GoogleDestinationsMap from './GoogleDestinationsMap';
import AddressReportBeta from './AddressReportBeta';
import CorrectCoordinatesModal from './CorrectCoordinatesModal';
import CorrectFullAddressModal from './CorrectFullAddressModal';
import { useAuth } from '../../AuthContext';

const getDestinationTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
        'F': 'F — Firm',
        'G': 'G — General Delivery',
        'H': 'H — High-rise',
        'P': 'P — PO Box',
        'R': 'R — Rural Route',
        'S': 'S — Street'
    };
    return labels[type] || type;
};

const getDestinationTypeDescription = (type: string): string => {
    const descriptions: Record<string, string> = {
        'F': 'Firm — The address belongs to a specific company or organization at that location (e.g., a named business building)',
        'G': 'General Delivery — A temporary mail service for people without a permanent address, typically at a post office',
        'H': 'High-rise — A multi-unit building such as an apartment complex, condo, or office tower where a unit number is expected',
        'P': 'PO Box — A locked box at a post office, not a physical delivery location',
        'R': 'Rural Route — A mail route in a rural area, typically without a traditional street address',
        'S': 'Street — A standard street address with a house or building number on a named street'
    };
    return descriptions[type] || 'Unknown destination type';
};

const AddressDetails: React.FC = () => {
    const { traceId } = useParams<{ traceId: string }>();
    const navigate = useNavigate();
    const { userInfo } = useAuth();
    const [address, setAddress] = useState<Address | null>(null);
    const [showCorrectCoordinates, setShowCorrectCoordinates] = useState(false);
    const [showCorrectFullAddress, setShowCorrectFullAddress] = useState(false);
    const [correctionMessage, setCorrectionMessage] = useState<string | null>(null);
    const [googleInfo, setGoogleInfo] = useState<AddressGoogleInfo | null>(null);
    const [radarInfo, setRadarInfo] = useState<AddressRadarInfo | null>(null);
    const [uberInfo, setUberInfo] = useState<AddressUberInfo | null>(null);
    const [uberFreshDetails, setUberFreshDetails] = useState<any | null>(null);
    const [uberFreshError, setUberFreshError] = useState<any | null>(null);
    const [isLoadingUberFresh, setIsLoadingUberFresh] = useState(false);
    const [showOverviewMap, setShowOverviewMap] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
    const [isLoadingRadar, setIsLoadingRadar] = useState(false);
    const [isLoadingUber, setIsLoadingUber] = useState(false);
    const [isLoadingPODs, setIsLoadingPODs] = useState(false);
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [isLoadingGemini, setIsLoadingGemini] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'google' | 'radar' | 'uber' | 'diffs' | 'history' | 'notes' | 'pods' | 'report' | 'destinations' | 'gemini' | 'elastic'>('overview');
    const [pods, setPods] = useState<any[]>([]);
    const [diffsPods, setDiffsPods] = useState<any[]>([]);
    const [isLoadingDiffsPods, setIsLoadingDiffsPods] = useState(false);
    const [selectedPod, setSelectedPod] = useState<any | null>(null);
    const [reportData, setReportData] = useState<AddressReportResponse | null>(null);
    const [geminiLogs, setGeminiLogs] = useState<GeminiPreprocessingLogResponseDto[]>([]);

    const [isFullResponseCollapsed, setIsFullResponseCollapsed] = useState(true);
    const [isValidationRequestCollapsed, setIsValidationRequestCollapsed] = useState(true);
    const [isRadarValidateResponseCollapsed, setIsRadarValidateResponseCollapsed] = useState(true);
    const [isForwardGeocodeRequestCollapsed, setIsForwardGeocodeRequestCollapsed] = useState(true);
    const [isRadarForwardResponseCollapsed, setIsRadarForwardResponseCollapsed] = useState(true);
    const [isGoogleRequestCollapsed, setIsGoogleRequestCollapsed] = useState(true);
    const [isGoogleValidationResponseCollapsed, setIsGoogleValidationResponseCollapsed] = useState(true);
    const [isGoogleGeocodeResponseCollapsed, setIsGoogleGeocodeResponseCollapsed] = useState(true);
    const [isGoogleGeocodeDestinationsResponseCollapsed, setIsGoogleGeocodeDestinationsResponseCollapsed] = useState(true);
    const [isGoogleFullResponseCollapsed, setIsGoogleFullResponseCollapsed] = useState(true);
    const [isUberRequestCollapsed, setIsUberRequestCollapsed] = useState(true);
    const [isUberResponseCollapsed, setIsUberResponseCollapsed] = useState(true);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
    const [previousTab, setPreviousTab] = useState<string>('');
    const [validationHistory, setValidationHistory] = useState<any[]>([]);
    const [isLoadingValidationHistory, setIsLoadingValidationHistory] = useState(false);
    const [expandedValidationHistoryId, setExpandedValidationHistoryId] = useState<string | null>(null);

    // Track tab changes and refresh overview when returning to it from other tabs
    useEffect(() => {
        if (activeTab === 'overview' && previousTab && previousTab !== 'overview' && traceId && !isLoading) {
            console.log(`Refreshing overview after returning from ${previousTab} tab`);
            const refreshProfile = async () => {
                try {
                    const data = await AddressManagementService.getAddressProfile(traceId);
                    setAddress(data);
                    console.log('Overview data refreshed successfully');
                } catch (err: any) {
                    console.error('Failed to refresh overview data:', err);
                }
            };
            refreshProfile();
        }
        setPreviousTab(activeTab);
    }, [activeTab, traceId]);

    // Track when history tab is activated to force refresh
    useEffect(() => {
        if (activeTab === 'history' && traceId) {
            console.log(`History tab activated for traceId: ${traceId}`);
            // Use timestamp to ensure fresh component mount only when tab is activated
            setHistoryRefreshKey(Date.now());
        }
    }, [activeTab]);

    // Initial load: Get Address Profile
    useEffect(() => {
        const fetchProfile = async () => {
            if (!traceId) return;
            setIsLoading(true);
            setError(null);
            try {
                console.log(`Fetching address profile for traceId: ${traceId}`);
                const data = await AddressManagementService.getAddressProfile(traceId);
                setAddress(data);
                console.log(`Successfully fetched address profile for traceId: ${traceId}`);
            } catch (err: any) {
                setError(err?.response?.data?.message || err.message || "Failed to load address details");
                console.error(`Failed to fetch address profile for traceId ${traceId}:`, err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProfile();
    }, [traceId]);

    // Load Google Info on tab switch
    useEffect(() => {
        if ((activeTab === 'google' || activeTab === 'diffs' || activeTab === 'destinations') && traceId && !isLoadingGoogle) {
            const fetchGoogle = async () => {
                setIsLoadingGoogle(true);
                try {
                    console.log(`Fetching Google info for traceId: ${traceId}`);
                    const data = await AddressManagementService.getAddressGoogleInfo(traceId);
                    setGoogleInfo(data);
                } catch (err: any) {
                    console.error("Failed to load Google info", err);
                } finally {
                    setIsLoadingGoogle(false);
                }
            };
            fetchGoogle();
        }
    }, [activeTab, traceId]);

    // Load Radar Info on tab switch
    useEffect(() => {
        if (activeTab === 'radar' && traceId && !isLoadingRadar) {
            const fetchRadar = async () => {
                setIsLoadingRadar(true);
                try {
                    console.log(`Fetching Radar info for traceId: ${traceId}`);
                    const data = await AddressManagementService.getAddressRadarInfo(traceId);
                    setRadarInfo(data);
                } catch (err: any) {
                    console.error("Failed to load Radar info", err);
                } finally {
                    setIsLoadingRadar(false);
                }
            };
            fetchRadar();
        }
    }, [activeTab, traceId]);

    // Load Uber Info on tab switch
    useEffect(() => {
        if (activeTab === 'uber' && traceId && !isLoadingUber) {
            const fetchUber = async () => {
                setIsLoadingUber(true);
                try {
                    console.log(`Fetching Uber info for traceId: ${traceId}`);
                    const data = await AddressManagementService.getAddressUberInfo(traceId);
                    setUberInfo(data);
                } catch (err: any) {
                    console.error("Failed to load Uber info", err);
                } finally {
                    setIsLoadingUber(false);
                }
            };
            fetchUber();
        }
    }, [activeTab, traceId]);

    // Load PODs on tab switch
    useEffect(() => {
        if (activeTab === 'pods' && traceId && !isLoadingPODs) {
            const fetchPODs = async () => {
                setIsLoadingPODs(true);
                try {
                    console.log(`Fetching PODs for traceId: ${traceId}`);
                    const response = await PodsService.getPODsByTraceId(traceId);
                    const data = response.data;

                    if (Array.isArray(data) && data.length > 0) {
                        const sortedData = data.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                        setPods(sortedData);
                    } else {
                        setPods([]);
                    }
                } catch (err: any) {
                    console.error("Failed to load PODs", err);
                    setPods([]);
                } finally {
                    setIsLoadingPODs(false);
                }
            };
            fetchPODs();
        }
    }, [activeTab, traceId]);

    // Load Address Report on tab switch
    useEffect(() => {
        if (activeTab === 'report' && traceId && !isLoadingReport && !reportData) {
            const fetchReport = async () => {
                setIsLoadingReport(true);
                try {
                    console.log(`Fetching Address Report for traceId: ${traceId}`);
                    const data = await AddressManagementService.getAddressReport(traceId);
                    setReportData(data);
                } catch (err: any) {
                    console.error("Failed to load Address Report", err);
                } finally {
                    setIsLoadingReport(false);
                }
            };
            fetchReport();
        }
    }, [activeTab, traceId]);

    // Load Gemini Logs on tab switch
    useEffect(() => {
        if (activeTab === 'gemini' && traceId && !isLoadingGemini) {
            const fetchGeminiLogs = async () => {
                setIsLoadingGemini(true);
                try {
                    console.log(`Fetching Gemini logs for traceId: ${traceId}`);
                    const data = await AddressManagementService.getGeminiLogs(traceId);
                    setGeminiLogs(data);
                } catch (err: any) {
                    console.error("Failed to load Gemini logs", err);
                    setGeminiLogs([]);
                } finally {
                    setIsLoadingGemini(false);
                }
            };
            fetchGeminiLogs();
        }
    }, [activeTab, traceId]);

    // Load Elastic Logs on tab switch
    useEffect(() => {
        if (activeTab === 'elastic' && traceId && !isLoadingValidationHistory) {
            const fetchValidationHistory = async () => {
                setIsLoadingValidationHistory(true);
                try {
                    console.log(`Fetching elastic logs for traceId: ${traceId}`);
                    const data = await AddressManagementService.getElasticLogs(traceId);
                    setValidationHistory(data);
                } catch (err: any) {
                    console.error("Failed to load elastic logs", err);
                    setValidationHistory([]);
                } finally {
                    setIsLoadingValidationHistory(false);
                }
            };
            fetchValidationHistory();
        }
    }, [activeTab, traceId]);

    const toggleValidationHistoryItem = (id: string) => {
        setExpandedValidationHistoryId(expandedValidationHistoryId === id ? null : id);
    };

    const handleCoordinatesCorrected = async (response: CorrectCoordinatesResponse) => {
        setShowCorrectCoordinates(false);
        setCorrectionMessage(response.message);
        if (traceId) {
            try {
                const data = await AddressManagementService.getAddressProfile(traceId);
                setAddress(data);
            } catch (err) {
                console.error('Failed to refresh profile after coordinate correction:', err);
            }
        }
    };

    const handleFullAddressCorrected = (response: CorrectFullAddressResponse) => {
        setShowCorrectFullAddress(false);
        // Surface which outcome the correction took: the old record was either superseded
        // (invalid) or kept live (valid on both providers). The message survives the
        // navigation below because the route change re-renders this same component.
        setCorrectionMessage(
            response.supersededOldAddress
                ? `Correction applied - the previous record (${response.previousTraceId}) was superseded and its input now redirects here.`
                : `Correction applied - the previous record (${response.previousTraceId}) is a valid address and was kept live.`
        );
        // The correction created (or reused) a record with a new trace ID - navigate to it
        if (response.traceId) {
            navigate(`/address-management/${response.traceId}`);
        }
    };

    const handleFetchFreshUberDetails = async () => {
        if (!traceId) return;

        setIsLoadingUberFresh(true);
        setUberFreshDetails(null);
        setUberFreshError(null);

        try {
            console.log(`Fetching fresh Uber address details for traceId: ${traceId}`);
            const data = await AddressManagementService.getFreshUberAddressDetails(traceId);
            setUberFreshDetails(data);
        } catch (err: any) {
            // The API forwards Uber's own error payload ({ code, message }) when available
            setUberFreshError(err?.response?.data || { message: err.message || 'Failed to fetch Uber address details' });
            console.error(`Failed to fetch fresh Uber details for traceId ${traceId}:`, err);
        } finally {
            setIsLoadingUberFresh(false);
        }
    };

    const handleRefreshAddress = async () => {
        if (!traceId) return;

        setShowRefreshConfirm(false);
        setIsRefreshing(true);
        setError(null);

        try {
            console.log(`Refreshing address for traceId: ${traceId}`);
            await AddressManagementService.refreshAddress(traceId);

            // Reload the address profile after refresh
            const data = await AddressManagementService.getAddressProfile(traceId);
            setAddress(data);

            // Clear cached data to force reload on next tab visit
            setGoogleInfo(null);
            setRadarInfo(null);
            setUberInfo(null);
            setUberFreshDetails(null);
            setUberFreshError(null);
            setReportData(null);
            setGeminiLogs([]);

            console.log(`Successfully refreshed address for traceId: ${traceId}`);
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || "Failed to refresh address");
            console.error(`Failed to refresh address for traceId ${traceId}:`, err);
        } finally {
            setIsRefreshing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="container-fluid py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading address details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid py-5">
                <div className="alert alert-danger">
                    <h4 className="alert-heading"><i className="bi bi-exclamation-triangle-fill me-2"></i>Error</h4>
                    <p>{error}</p>
                    <hr />
                    <Link to="/address-management" className="btn btn-outline-danger">Return to List</Link>
                </div>
            </div>
        );
    }

    if (!address) return null;

    const deliveredCoordinates = getLatestDeliveredCoordinates(address);
    const podMarkers = getAllDeliveredCoordinates(address);

    return (
        <div className="container-fluid py-4">
            {/* Header / Breadcrumb */}
            <div className="mb-4">
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><Link to="/address-management">Address Management</Link></li>
                        <li className="breadcrumb-item active" aria-current="page">{traceId}</li>
                    </ol>
                </nav>

                {address.supersededByAddressId != null && (
                    <div className="alert alert-warning d-flex justify-content-between align-items-center">
                        <div>
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <strong>This address was corrected.</strong> This record has been superseded by a
                            newer record and is kept for history; validations of this input resolve to the
                            corrected address.
                        </div>
                        {address.supersededByTraceId && (
                            <Link
                                to={`/address-management/${address.supersededByTraceId}`}
                                className="btn btn-warning btn-sm flex-shrink-0 ms-3"
                            >
                                <i className="bi bi-arrow-right-circle me-1"></i>Open Current Record
                            </Link>
                        )}
                    </div>
                )}

                {correctionMessage && (
                    <div className="alert alert-success alert-dismissible">
                        <i className="bi bi-check-circle-fill me-2"></i>{correctionMessage}
                        <button type="button" className="btn-close" onClick={() => setCorrectionMessage(null)}></button>
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h2 className="fw-bold mb-1">{address.street1}</h2>
                        <p className="text-muted mb-3">
                            {address.street2 && <span>{address.street2}, </span>}
                            {address.city}, {address.stateCode} {address.zip}
                        </p>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-3">
                        <div className="d-flex gap-2">
                            {address.supersededByAddressId == null && (
                                <>
                                    <button
                                        className="btn btn-outline-success"
                                        onClick={() => setShowCorrectCoordinates(true)}
                                    >
                                        <i className="bi bi-geo-alt me-2"></i>Correct Coordinates
                                    </button>
                                    <button
                                        className="btn btn-success"
                                        onClick={() => setShowCorrectFullAddress(true)}
                                    >
                                        <i className="bi bi-house-gear me-2"></i>Correct Address
                                    </button>
                                    {/* Superseded records can't be re-evaluated either: the API refuses
                                        (409) since their components are already known to be wrong */}
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => setShowRefreshConfirm(true)}
                                        disabled={isRefreshing}
                                    >
                                        {isRefreshing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Re-evaluating Address...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-arrow-clockwise me-2"></i>Re-evaluate Address
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="text-end text-muted small">
                            <div>ID: <span className="font-monospace">{address.id}</span></div>
                            <div>Created: {new Date(address.createdAt).toLocaleString()}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs and Content Layout */}
            <div className="row">
                {/* Vertical Tabs Sidebar */}
                <div className="col-md-3 col-lg-2">
                    <div className="nav flex-column nav-pills sticky-top" style={{ top: '20px' }}>
                        <button
                            className={`nav-link text-start ${activeTab === 'overview' ? 'active' : ''}`}
                            onClick={() => setActiveTab('overview')}
                        >
                            <i className="bi bi-house me-1"></i>Overview
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            <i className="bi bi-clock-history me-1"></i>History
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'notes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('notes')}
                        >
                            <i className="bi bi-journal-text me-1"></i>Notes
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'google' ? 'active' : ''}`}
                            onClick={() => setActiveTab('google')}
                        >
                            <i className="bi bi-google me-1"></i>Google API
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'radar' ? 'active' : ''}`}
                            onClick={() => setActiveTab('radar')}
                        >
                            <i className="bi bi-broadcast me-1"></i>Radar API
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'uber' ? 'active' : ''}`}
                            onClick={() => setActiveTab('uber')}
                        >
                            <i className="bi bi-taxi-front me-1"></i>Uber API <span className="badge bg-warning text-dark ms-1 small">Beta</span>
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'diffs' ? 'active' : ''}`}
                            onClick={() => setActiveTab('diffs')}
                        >
                            <i className="bi bi-layout-split me-1"></i>API Differences
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'pods' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pods')}
                        >
                            <i className="bi bi-images me-1"></i>PODs
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'destinations' ? 'active' : ''}`}
                            onClick={() => setActiveTab('destinations')}
                        >
                            <i className="bi bi-building me-1"></i>Google Destinations <span className="badge bg-warning text-dark ms-1 small">Beta</span>
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'report' ? 'active' : ''}`}
                            onClick={() => setActiveTab('report')}
                        >
                            <i className="bi bi-file-earmark-text me-1"></i>Address Report
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'gemini' ? 'active' : ''}`}
                            onClick={() => setActiveTab('gemini')}
                        >
                            <i className="bi bi-robot me-1"></i>Gemini Logs
                        </button>
                        <button
                            className={`nav-link text-start ${activeTab === 'elastic' ? 'active' : ''}`}
                            onClick={() => setActiveTab('elastic')}
                        >
                            <i className="bi bi-database me-1"></i>Elastic Logs
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="col-md-9 col-lg-10">
                    <div className="tab-content">

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="row g-4">
                        <div className="col-md-6">
                            <div className="card shadow-sm h-100">
                                <div className="card-header bg-light fw-bold">Address Details</div>
                                <div className="card-body">
                                    <table className="table table-sm table-borderless">
                                        <tbody>
                                            <tr><td className="text-muted w-25">Street 1:</td><td className="fw-medium">{address.street1}</td></tr>
                                            <tr><td className="text-muted">Street 2:</td><td className="fw-medium">{address.street2 || '-'}</td></tr>
                                            <tr><td className="text-muted">City/State/Zip:</td><td className="fw-medium">{address.city}, {address.stateCode} {address.zip}</td></tr>
                                            <tr><td className="text-muted">Country:</td><td className="fw-medium">{address.country}</td></tr>
                                            <tr><td className="text-muted">Coordinates:</td><td className="fw-medium">{address.latitude}, {address.longitude}</td></tr>
                                            <tr><td className="text-muted">Type:</td><td className="fw-medium">{address.addressType || '-'}</td></tr>
                                            <tr>
                                                <td className="text-muted">Is Apartment:</td>
                                                <td className="fw-medium">
                                                    {address.isApartment ? (
                                                        <span className="badge bg-success">Yes</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">No</span>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Destination Type:</td>
                                                <td className="fw-medium">
                                                    {address.destinationType ? (
                                                        <Tooltip
                                                            title={getDestinationTypeDescription(address.destinationType)}
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <span style={{ cursor: 'help', borderBottom: '1px dotted #007bff' }}>
                                                                {getDestinationTypeLabel(address.destinationType)}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Coordinate Source:</td>
                                                <td className="fw-medium">{address.coordinateSource || '-'}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Correction:</td>
                                                <td className="fw-medium">
                                                    {address.supersededByAddressId != null ? (
                                                        address.supersededByTraceId ? (
                                                            <Link to={`/address-management/${address.supersededByTraceId}`} className="text-decoration-none">
                                                                <span className="badge bg-warning text-dark">
                                                                    <i className="bi bi-arrow-right-circle me-1"></i>Corrected — open current record
                                                                </span>
                                                            </Link>
                                                        ) : (
                                                            <span className="badge bg-warning text-dark">Corrected</span>
                                                        )
                                                    ) : (
                                                        <span className="badge bg-success">Current</span>
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="card shadow-sm h-100">
                                <div className="card-header bg-light fw-bold">Validation Summary</div>
                                <div className="card-body">
                                    {/* Validation Status Cards */}
                                    <div className="row g-2 mb-4">
                                        <div className="col-6">
                                            <div className="p-3 border rounded bg-light">
                                                <div className="small text-muted mb-2">Google Possible Next Action</div>
                                                <div className="fw-bold text-dark">{address.googlePossibleNextAction ?? '-'}</div>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="p-3 border rounded bg-light">
                                                <div className="small text-muted mb-2">Radar Status</div>
                                                <div className="fw-bold text-dark">{address.radarVerificationStatus ?? '-'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-4">
                                        <div className="col-6">
                                            <div className="p-3 border rounded bg-light">
                                                <div className="small text-muted mb-2">Radar Geoconfidence</div>
                                                <div className="fw-bold text-dark">{address.radarGeoconfidence ?? '-'}</div>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="p-3 border rounded bg-light">
                                                <div className="small text-muted mb-2">Internal Geofence Confidence</div>
                                                <div className="fw-bold text-dark">{address.geoFenceConfidence ?? '-'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Coordinates Comparison */}
                                    <div className="mb-4">
                                        <label className="small text-uppercase text-muted fw-bold mb-2 d-block">Coordinates Comparison</label>
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted small ps-0">Google:</td>
                                                    <td className="text-end pe-0 small">{address.googleLatitude && address.googleLongitude ? `${address.googleLatitude}, ${address.googleLongitude}` : '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted small ps-0">Radar:</td>
                                                    <td className="text-end pe-0 small">{address.radarLatitude && address.radarLongitude ? `${address.radarLatitude}, ${address.radarLongitude}` : '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted small ps-0">Uber:</td>
                                                    <td className="text-end pe-0 small">{address.uberLatitude && address.uberLongitude ? `${address.uberLatitude}, ${address.uberLongitude}` : '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted small ps-0">Delivered:</td>
                                                    <td className="text-end pe-0 small">{deliveredCoordinates ? `${deliveredCoordinates.lat}, ${deliveredCoordinates.lng}` : '-'}</td>
                                                </tr>
                                                <tr className="fw-bold border-top">
                                                    <td className="text-muted small ps-0">Used:</td>
                                                    <td className="text-end pe-0 small">{address.latitude && address.longitude ? `${address.latitude}, ${address.longitude}` : '-'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <button
                                            className="btn btn-sm btn-outline-primary mt-2"
                                            onClick={() => setShowOverviewMap(true)}
                                        >
                                            <i className="bi bi-map me-2"></i>
                                            Preview on the map
                                        </button>
                                    </div>

                                    {/* Validation Feedback */}
                                    {address.validationFeedback && (
                                        <div className="mb-0">
                                            <label className="small text-uppercase text-muted fw-bold mb-2 d-block">Feedback</label>
                                            <div className="alert alert-light border small mb-0 py-2 px-3">
                                                {address.validationFeedback}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                )}

                {/* Provider Coordinates Map Preview Modal */}
                <CoordinatesComparisonMapModal
                    show={showOverviewMap}
                    onClose={() => setShowOverviewMap(false)}
                    fullAddress={`${address.street1}${address.street2 ? ', ' + address.street2 : ''}, ${address.city}, ${address.stateCode} ${address.zip}`}
                    traceId={traceId}
                    googleCoordinates={
                        address.googleLatitude && address.googleLongitude
                            ? { lat: address.googleLatitude, lng: address.googleLongitude }
                            : null
                    }
                    radarCoordinates={
                        address.radarLatitude && address.radarLongitude
                            ? { lat: address.radarLatitude, lng: address.radarLongitude }
                            : null
                    }
                    uberCoordinates={
                        address.uberLatitude && address.uberLongitude
                            ? { lat: address.uberLatitude, lng: address.uberLongitude }
                            : null
                    }
                    deliveredCoordinates={deliveredCoordinates}
                    podMarkers={podMarkers}
                />

                {/* History Tab */}
                {activeTab === 'history' && traceId && (
                    <AddressHistoryTimeline traceId={traceId} refreshTrigger={historyRefreshKey} />
                )}

                {/* Notes Tab */}
                {activeTab === 'notes' && traceId && (
                    <AddressNotes traceId={traceId} />
                )}

                {/* Google API Tab */}
                {activeTab === 'google' && (
                    <div className="row g-4">
                        {isLoadingGoogle ? (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-2 text-muted">Loading Google details...</p>
                            </div>
                        ) : !googleInfo ? (
                            <div className="col-12 py-5 text-center text-muted">No Google information available.</div>
                        ) : (
                            <div className="col-12">
                                {/* Validation Status */}
                                <div className="card shadow-sm mb-4">
                                    <div className="card-body">
                                        <h6 className="mb-3">Google Address Validation</h6>
                                        <div className="row g-3">
                                            <div className="col-6">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Validation Status</div>
                                                    <div className="fw-bold">
                                                        <span className={`badge ${address.isGoogleValid ? 'bg-success' : 'bg-danger'}`}>
                                                            <i className={`bi bi-${address.isGoogleValid ? 'check-circle' : 'x-circle'} me-1`}></i>
                                                            {address.isGoogleValid ? 'Valid' : 'Invalid'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Possible Next Action</div>
                                                    <div className="fw-bold">
                                                        {address.googlePossibleNextAction ? (
                                                            <span className="badge bg-info text-dark">{address.googlePossibleNextAction}</span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Google Request Section - Full Width at Top */}
                                <div className="row g-3 mb-4">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsGoogleRequestCollapsed(!isGoogleRequestCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isGoogleRequestCollapsed ? 'right' : 'down'}`}></i>
                                                    Google Request
                                                </button>
                                            </div>
                                            {!isGoogleRequestCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!googleInfo.googleRequest) return null;
                                                            try {
                                                                return JSON.parse(googleInfo.googleRequest);
                                                            } catch {
                                                                return googleInfo.googleRequest;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Response Sections - 3 Columns */}
                                <div className="row g-3 mb-4">
                                    <div className="col-lg-4">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsGoogleGeocodeDestinationsResponseCollapsed(!isGoogleGeocodeDestinationsResponseCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isGoogleGeocodeDestinationsResponseCollapsed ? 'right' : 'down'}`}></i>
                                                    Google Geocode Destinations Response
                                                </button>
                                            </div>
                                            {!isGoogleGeocodeDestinationsResponseCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!googleInfo.googleGeocodeDestinationsResponse) return null;
                                                            try {
                                                                return JSON.parse(googleInfo.googleGeocodeDestinationsResponse);
                                                            } catch {
                                                                return googleInfo.googleGeocodeDestinationsResponse;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsGoogleGeocodeResponseCollapsed(!isGoogleGeocodeResponseCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isGoogleGeocodeResponseCollapsed ? 'right' : 'down'}`}></i>
                                                    Google Geocode Response
                                                </button>
                                            </div>
                                            {!isGoogleGeocodeResponseCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!googleInfo.googleGeocodeResponse) return null;
                                                            try {
                                                                return JSON.parse(googleInfo.googleGeocodeResponse);
                                                            } catch {
                                                                return googleInfo.googleGeocodeResponse;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsGoogleValidationResponseCollapsed(!isGoogleValidationResponseCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isGoogleValidationResponseCollapsed ? 'right' : 'down'}`}></i>
                                                    Google Response
                                                </button>
                                            </div>
                                            {!isGoogleValidationResponseCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!googleInfo.googleResponse) return null;
                                                            try {
                                                                return JSON.parse(googleInfo.googleResponse);
                                                            } catch {
                                                                return googleInfo.googleResponse;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Collapsible Full Response */}
                                <div className="card">
                                    <div className="card-header">
                                        <button
                                            className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                            onClick={() => setIsGoogleFullResponseCollapsed(!isGoogleFullResponseCollapsed)}
                                        >
                                            <i className={`bi bi-chevron-${isGoogleFullResponseCollapsed ? 'right' : 'down'}`}></i>
                                            Full Response (Complete)
                                        </button>
                                    </div>
                                    {!isGoogleFullResponseCollapsed && (
                                        <div className="card-body">
                                            <JsonViewer
                                                title="Google API Response (Complete)"
                                                data={(() => {
                                                    try {
                                                        return {
                                                            request: googleInfo.googleRequest ? JSON.parse(googleInfo.googleRequest) : null,
                                                            validationResponse: googleInfo.googleResponse ? JSON.parse(googleInfo.googleResponse) : null,
                                                            geocodeResponse: googleInfo.googleGeocodeResponse ? JSON.parse(googleInfo.googleGeocodeResponse) : null
                                                        };
                                                    } catch {
                                                        return {
                                                            request: googleInfo.googleRequest,
                                                            validationResponse: googleInfo.googleResponse,
                                                            geocodeResponse: googleInfo.googleGeocodeResponse
                                                        };
                                                    }
                                                })()}
                                                maxHeight="700px"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Radar API Tab */}
                {activeTab === 'radar' && (
                    <div className="row g-4">
                        {isLoadingRadar ? (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-2 text-muted">Loading Radar details...</p>
                            </div>
                        ) : !radarInfo ? (
                            <div className="col-12 py-5 text-center text-muted">No Radar information available.</div>
                        ) : (
                            <div className="col-12">
                                {/* Validation Status */}
                                <div className="card shadow-sm mb-4">
                                    <div className="card-body">
                                        <h6 className="mb-3">Radar Address Geocoding</h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Validation Status</div>
                                                    <div className="fw-bold">
                                                        <span className={`badge ${address.isRadarValid ? 'bg-success' : 'bg-danger'}`}>
                                                            <i className={`bi bi-${address.isRadarValid ? 'check-circle' : 'x-circle'} me-1`}></i>
                                                            {address.isRadarValid ? 'Valid' : 'Invalid'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Verification Status</div>
                                                    <div className="fw-bold">
                                                        {address.radarVerificationStatus ? (
                                                            <span className="badge bg-info text-dark">{address.radarVerificationStatus}</span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Geoconfidence</div>
                                                    <div className="fw-bold">
                                                        {address.radarGeoconfidence ? (
                                                            <span className="badge bg-info text-dark">{address.radarGeoconfidence}</span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* API Sections */}
                                <div className="row g-3 mb-4">
                                    <div className="col-lg-6">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsValidationRequestCollapsed(!isValidationRequestCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isValidationRequestCollapsed ? 'right' : 'down'}`}></i>
                                                    Validation Request
                                                </button>
                                            </div>
                                            {!isValidationRequestCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!radarInfo.radarRequest) return null;
                                                            try {
                                                                const parsed = JSON.parse(radarInfo.radarRequest);
                                                                return { ValidationRequest: parsed.ValidationRequest };
                                                            } catch {
                                                                return null;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsRadarValidateResponseCollapsed(!isRadarValidateResponseCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isRadarValidateResponseCollapsed ? 'right' : 'down'}`}></i>
                                                    Radar Validate Response
                                                </button>
                                            </div>
                                            {!isRadarValidateResponseCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!radarInfo.radarResponse) return null;
                                                            try {
                                                                const parsed = JSON.parse(radarInfo.radarResponse);
                                                                return { RadarForwardResponse: parsed.RadarForwardResponse };
                                                            } catch {
                                                                return null;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3 mb-4">
                                    <div className="col-lg-6">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsForwardGeocodeRequestCollapsed(!isForwardGeocodeRequestCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isForwardGeocodeRequestCollapsed ? 'right' : 'down'}`}></i>
                                                    Forward Geocode Request
                                                </button>
                                            </div>
                                            {!isForwardGeocodeRequestCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!radarInfo.radarRequest) return null;
                                                            try {
                                                                const parsed = JSON.parse(radarInfo.radarRequest);
                                                                return { ForwardGeoCodeRequest: parsed.ForwardGeoCodeRequest };
                                                            } catch {
                                                                return null;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="card">
                                            <div className="card-header">
                                                <button
                                                    className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                    onClick={() => setIsRadarForwardResponseCollapsed(!isRadarForwardResponseCollapsed)}
                                                >
                                                    <i className={`bi bi-chevron-${isRadarForwardResponseCollapsed ? 'right' : 'down'}`}></i>
                                                    Radar Forward Response
                                                </button>
                                            </div>
                                            {!isRadarForwardResponseCollapsed && (
                                                <div className="card-body">
                                                    <JsonViewer
                                                        title=""
                                                        data={(() => {
                                                            if (!radarInfo.radarResponse) return null;
                                                            try {
                                                                const parsed = JSON.parse(radarInfo.radarResponse);
                                                                return parsed.RadarForwardResponse || null;
                                                            } catch {
                                                                return null;
                                                            }
                                                        })()}
                                                        maxHeight="400px"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Collapsible Full Response */}
                                <div className="card">
                                    <div className="card-header">
                                        <button
                                            className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                            onClick={() => setIsFullResponseCollapsed(!isFullResponseCollapsed)}
                                        >
                                            <i className={`bi bi-chevron-${isFullResponseCollapsed ? 'right' : 'down'}`}></i>
                                            Full Response (Complete)
                                        </button>
                                    </div>
                                    {!isFullResponseCollapsed && (
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <div className="col-lg-6">
                                                    <JsonViewer
                                                        title="Complete Request"
                                                        data={radarInfo.radarRequest}
                                                        maxHeight="600px"
                                                    />
                                                </div>
                                                <div className="col-lg-6">
                                                    <JsonViewer
                                                        title="Complete Response"
                                                        data={radarInfo.radarResponse}
                                                        maxHeight="600px"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Uber API Tab */}
                {activeTab === 'uber' && (
                    <div className="row g-4">
                        {isLoadingUber ? (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-2 text-muted">Loading Uber details...</p>
                            </div>
                        ) : (
                            <div className="col-12">
                                {/* Validation Status */}
                                <div className="card shadow-sm mb-4">
                                    <div className="card-body">
                                        <h6 className="mb-3">Uber Address Geocoding</h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Validation Status</div>
                                                    <div className="fw-bold">
                                                        <span className={`badge ${address.isUberValid ? 'bg-success' : 'bg-danger'}`}>
                                                            <i className={`bi bi-${address.isUberValid ? 'check-circle' : 'x-circle'} me-1`}></i>
                                                            {address.isUberValid ? 'Valid' : 'Invalid'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Coordinates</div>
                                                    <div className="fw-bold">
                                                        {address.uberLatitude && address.uberLongitude ? (
                                                            <span>{address.uberLatitude}, {address.uberLongitude}</span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="p-3 border rounded bg-light">
                                                    <div className="small text-muted mb-2">Place ID</div>
                                                    <div className="fw-bold small font-monospace" style={{ wordBreak: 'break-all' }}>
                                                        {address.uberPlaceId || <span className="text-muted fw-normal">-</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Fresh Address Details */}
                                <div className="card shadow-sm mb-4">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div>
                                                <h6 className="mb-1">Live Address Details</h6>
                                                <div className="small text-muted">
                                                    Calls the Uber Details API with the stored place ID to fetch fresh address details.
                                                </div>
                                            </div>
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={handleFetchFreshUberDetails}
                                                disabled={isLoadingUberFresh}
                                            >
                                                {isLoadingUberFresh ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Fetching Address Details...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-cloud-download me-2"></i>Get Fresh Address Details
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {uberFreshError && (
                                            <div className="alert alert-danger mt-3 mb-0">
                                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                <strong>Uber API call failed{uberFreshError.code ? ` (${uberFreshError.code})` : ''}:</strong>{' '}
                                                {uberFreshError.message || 'Unknown error'}
                                            </div>
                                        )}

                                        {uberFreshDetails && (
                                            <div className="mt-3">
                                                <JsonViewer
                                                    title="Uber Address Details Response"
                                                    data={uberFreshDetails}
                                                    maxHeight="500px"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Stored API Sections */}
                                {!uberInfo ? (
                                    <div className="py-4 text-center text-muted">No stored Uber information available.</div>
                                ) : (
                                    <div className="row g-3">
                                        <div className="col-lg-6">
                                            <div className="card">
                                                <div className="card-header">
                                                    <button
                                                        className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                        onClick={() => setIsUberRequestCollapsed(!isUberRequestCollapsed)}
                                                    >
                                                        <i className={`bi bi-chevron-${isUberRequestCollapsed ? 'right' : 'down'}`}></i>
                                                        Uber Geocode Request
                                                    </button>
                                                </div>
                                                {!isUberRequestCollapsed && (
                                                    <div className="card-body">
                                                        <JsonViewer
                                                            title=""
                                                            data={(() => {
                                                                if (!uberInfo.uberRequest) return null;
                                                                try {
                                                                    return JSON.parse(uberInfo.uberRequest);
                                                                } catch {
                                                                    return uberInfo.uberRequest;
                                                                }
                                                            })()}
                                                            maxHeight="400px"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="card">
                                                <div className="card-header">
                                                    <button
                                                        className="btn btn-link text-decoration-none p-0 fw-medium d-flex align-items-center gap-2"
                                                        onClick={() => setIsUberResponseCollapsed(!isUberResponseCollapsed)}
                                                    >
                                                        <i className={`bi bi-chevron-${isUberResponseCollapsed ? 'right' : 'down'}`}></i>
                                                        Uber Geocode Response
                                                    </button>
                                                </div>
                                                {!isUberResponseCollapsed && (
                                                    <div className="card-body">
                                                        <JsonViewer
                                                            title=""
                                                            data={(() => {
                                                                if (!uberInfo.uberResponse) return null;
                                                                try {
                                                                    return JSON.parse(uberInfo.uberResponse);
                                                                } catch {
                                                                    return uberInfo.uberResponse;
                                                                }
                                                            })()}
                                                            maxHeight="400px"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* API Differences Tab */}
                {activeTab === 'diffs' && (
                    <div className="row g-4">
                        {isLoadingGoogle ? (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-2 text-muted">Loading Difference details...</p>
                            </div>
                        ) : !googleInfo ? (
                            <div className="col-12 py-5 text-center text-muted">No difference information available (Google info missing).</div>
                        ) : (
                            <>
                                <div className="col-lg-6">
                                    <ComparisonMap
                                        googleCoordinates={
                                            address.latitude && address.longitude
                                                ? {
                                                    lat: address.latitude,
                                                    lng: address.longitude
                                                }
                                                : null
                                        }
                                        radarCoordinates={
                                            address.radarLatitude && address.radarLongitude
                                                ? {
                                                    lat: address.radarLatitude,
                                                    lng: address.radarLongitude
                                                }
                                                : null
                                        }
                                        podMarkers={diffsPods
                                            .filter((pod) => pod.latitude !== null && pod.longitude !== null)
                                            .map((pod) => ({
                                                id: pod.id,
                                                lat: pod.latitude!,
                                                lng: pod.longitude!,
                                                podUrl: pod.podUrl,
                                                trackingNumber: pod.trackingNumber,
                                                createdAt: pod.createdAt
                                            }))}
                                        address={`${address.street1}${address.street2 ? ', ' + address.street2 : ''}, ${address.city}, ${address.stateCode} ${address.zip}`}
                                        traceId={traceId}
                                        height="500px"
                                    />
                                </div>
                                <div className="col-lg-6">
                                    <div className="alert alert-info small mb-3">
                                        <i className="bi bi-info-circle-fill me-2"></i>
                                        This section highlights disparities between the Google Address Validation API and Google Geocoding API results.
                                    </div>
                                    <div className="mb-3 d-flex gap-2">
                                        <button
                                            className={`btn btn-sm ${diffsPods.length > 0 ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={async () => {
                                                if (diffsPods.length === 0 && traceId) {
                                                    setIsLoadingDiffsPods(true);
                                                    try {
                                                        const response = await PodsService.getPODsByTraceId(traceId);
                                                        const data = response.data;
                                                        if (Array.isArray(data) && data.length > 0) {
                                                            const sortedData = data.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                                                            setDiffsPods(sortedData);
                                                        } else {
                                                            setDiffsPods([]);
                                                        }
                                                    } catch (err) {
                                                        console.error('Failed to load PODs', err);
                                                        setDiffsPods([]);
                                                    } finally {
                                                        setIsLoadingDiffsPods(false);
                                                    }
                                                } else {
                                                    setDiffsPods([]);
                                                }
                                            }}
                                            disabled={isLoadingDiffsPods}
                                        >
                                            {isLoadingDiffsPods ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Loading PODs...
                                                </>
                                            ) : diffsPods.length > 0 ? (
                                                <>
                                                    <i className="bi bi-eye-slash me-2"></i>
                                                    Hide PODs ({diffsPods.length})
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-images me-2"></i>
                                                    Load PODs
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {diffsPods.length > 0 && (
                                        <div className="mb-3">
                                            <h6 className="mb-2 small">POD Images ({diffsPods.filter((pod) => pod.latitude !== null && pod.longitude !== null).length} with location)</h6>
                                            <div className="row g-1">
                                                {diffsPods.map((pod) => (
                                                    <div key={pod.id} className="col-4">
                                                        <div
                                                            className="card border-0 shadow-sm"
                                                            onClick={() => setSelectedPod(pod)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div
                                                                style={{
                                                                    paddingBottom: '75%',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    borderRadius: '4px',
                                                                }}
                                                            >
                                                                <img
                                                                    src={pod.podUrl}
                                                                    className="position-absolute w-100 h-100"
                                                                    alt={`POD ${pod.id}`}
                                                                    style={{
                                                                        objectFit: 'cover',
                                                                        top: 0,
                                                                        left: 0,
                                                                    }}
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                                                                    }}
                                                                />
                                                                {(pod.latitude !== null && pod.longitude !== null) && (
                                                                    <div
                                                                        className="position-absolute top-0 end-0 m-1 bg-primary text-white px-1 py-0 rounded"
                                                                        style={{ fontSize: '0.6rem' }}
                                                                    >
                                                                        <i className="bi bi-geo-alt-fill"></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="card-body p-1">
                                                                <small className="text-muted d-block" style={{ fontSize: '0.55rem' }}>
                                                                    #{pod.id}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <JsonViewer
                                        title="API Differences"
                                        data={googleInfo.googleApiDifferences}
                                        maxHeight="500px"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* PODs Tab */}
                {activeTab === 'pods' && (
                    <div>
                        {isLoadingPODs ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading PODs...</p>
                            </div>
                        ) : pods.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="bi bi-inbox fs-1 text-muted opacity-50"></i>
                                <p className="mt-2 text-muted">No PODs found for this address</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {pods.map((pod) => (
                                    <div key={pod.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                                        <div
                                            className="card h-100 border-0 shadow-sm pod-card"
                                            onClick={() => setSelectedPod(pod)}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    paddingBottom: '75%',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    borderRadius: '4px',
                                                }}
                                            >
                                                <img
                                                    src={pod.podUrl}
                                                    className="card-img-top position-absolute w-100 h-100"
                                                    alt={`POD ${pod.id}`}
                                                    style={{
                                                        objectFit: 'cover',
                                                        top: 0,
                                                        left: 0,
                                                    }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src =
                                                            'https://via.placeholder.com/400x300?text=No+Image';
                                                    }}
                                                />
                                            </div>
                                            <div className="card-body p-2">
                                                <small className="text-muted d-block mb-1">
                                                    <strong>ID:</strong> {pod.id}
                                                </small>
                                                <small className="text-primary fw-bold d-block mb-1">
                                                    {new Date(pod.createdAt).toLocaleString()}
                                                </small>
                                                {pod.trackingNumber && (
                                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem' }}>
                                                        <i className="bi bi-box-seam me-1"></i>
                                                        {pod.trackingNumber}
                                                    </small>
                                                )}
                                                {(pod.latitude !== null && pod.latitude !== undefined) &&
                                                 (pod.longitude !== null && pod.longitude !== undefined) && (
                                                    <small className="text-success d-block" style={{ fontSize: '0.7rem' }}>
                                                        <i className="bi bi-geo-alt-fill me-1"></i>
                                                        {pod.latitude.toFixed(4)}, {pod.longitude.toFixed(4)}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* POD Preview Modal */}
                        {selectedPod && (
                            <div
                                className="modal fade show"
                                style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                                tabIndex={-1}
                                role="dialog"
                                onClick={() => setSelectedPod(null)}
                            >
                                <div
                                    className="modal-dialog modal-xl modal-dialog-centered"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="modal-content border-0 shadow-lg">
                                        <div className="modal-header border-bottom-0">
                                            <h5 className="modal-title">POD Details</h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                aria-label="Close"
                                                onClick={() => setSelectedPod(null)}
                                            ></button>
                                        </div>
                                        <div
                                            className="modal-body text-center bg-light p-0 d-flex justify-content-center align-items-center"
                                            style={{
                                                minHeight: '400px',
                                                backgroundColor: '#f8f9fa',
                                            }}
                                        >
                                            <img
                                                src={selectedPod.podUrl}
                                                alt={`POD ${selectedPod.id}`}
                                                className="img-fluid"
                                                style={{ maxHeight: '70vh' }}
                                            />
                                        </div>
                                        <div className="modal-footer justify-content-between flex-column align-items-start">
                                            <div className="d-flex justify-content-between w-100">
                                                <div className="text-start">
                                                    <div className="mb-2">
                                                        <strong>ID:</strong> {selectedPod.id}
                                                    </div>
                                                    <div className="mb-2">
                                                        <strong>Created:</strong>{' '}
                                                        {new Date(selectedPod.createdAt).toLocaleString()}
                                                    </div>
                                                    <div className="mb-2">
                                                        <strong>Trace ID:</strong>{' '}
                                                        <span className="font-monospace text-muted small">
                                                            {selectedPod.addressTraceId}
                                                        </span>
                                                    </div>
                                                    {selectedPod.trackingNumber && (
                                                        <div className="mb-2">
                                                            <strong>Tracking Number:</strong>{' '}
                                                            <span className="font-monospace">
                                                                {selectedPod.trackingNumber}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {(selectedPod.latitude !== null && selectedPod.latitude !== undefined) &&
                                                     (selectedPod.longitude !== null && selectedPod.longitude !== undefined) && (
                                                        <div className="mb-2">
                                                            <strong>Delivery Location:</strong>{' '}
                                                            <span className="font-monospace small">
                                                                {selectedPod.latitude.toFixed(7)}, {selectedPod.longitude.toFixed(7)}
                                                            </span>
                                                            <a
                                                                href={`https://www.google.com/maps?q=${selectedPod.latitude},${selectedPod.longitude}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="ms-2 text-decoration-none"
                                                                title="View on Google Maps"
                                                            >
                                                                <i className="bi bi-geo-alt-fill text-primary"></i>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="d-flex align-items-start">
                                                    <a
                                                        href={selectedPod.podUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-outline-primary me-2"
                                                    >
                                                        <i className="bi bi-box-arrow-up-right me-2"></i>
                                                        Open Original
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setSelectedPod(null)}
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <style>{`
                            .pod-card:hover {
                                transform: translateY(-2px);
                            }
                        `}</style>
                    </div>
                )}

                {/* Google Destinations Tab */}
                {activeTab === 'destinations' && (
                    <div className="row g-4">
                        {isLoadingGoogle ? (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-2 text-muted">Loading Google Destinations...</p>
                            </div>
                        ) : !googleInfo?.googleGeocodeDestinationsResponse ? (
                            <div className="col-12 py-5 text-center text-muted">No Google Destinations data available.</div>
                        ) : (
                            <div className="col-12">
                                <GoogleDestinationsMap
                                    destinationsResponse={googleInfo.googleGeocodeDestinationsResponse}
                                    address={`${address.street1}${address.street2 ? ', ' + address.street2 : ''}, ${address.city}, ${address.stateCode} ${address.zip}`}
                                    height="600px"
                                />
                            </div>
                        )}
                    </div>
                )}

            </div>

            {/* Address Report Beta Tab */}
            {activeTab === 'report' && (
                <div className="row">
                    <div className="col-12">
                        <AddressReportBeta data={reportData} isLoading={isLoadingReport} podMarkers={podMarkers} />
                    </div>
                </div>
            )}

            {/* Gemini Logs Tab */}
            {activeTab === 'gemini' && (
                <div>
                    {isLoadingGemini ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading Gemini logs...</p>
                        </div>
                    ) : geminiLogs.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-inbox fs-1 text-muted opacity-50"></i>
                            <p className="mt-2 text-muted">No Gemini logs found for this address</p>
                        </div>
                    ) : (
                        <div className="row g-2">
                            {geminiLogs.map((log, index) => (
                                <div key={log.id} className="col-12">
                                    <div className="card shadow-sm">
                                        <div className="card-header d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: log.status?.toLocaleLowerCase() === 'success' ? '#d1e7dd' : log.status?.toLocaleLowerCase() === 'error' ? '#f8d7da' : '#fff3cd' }}>
                                            <div>
                                                <div className="fw-medium small mb-1">
                                                    <i className="bi bi-robot me-1"></i>
                                                    Log #{index + 1}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    {new Date(log.createdAt).toLocaleString()}
                                                    {log.processingTimeMs && <span> • {log.processingTimeMs}ms</span>}
                                                </div>
                                            </div>
                                            <span className={`badge ${log.status?.toLocaleLowerCase() === 'success' ? 'bg-success' : log.status === 'error' ? 'bg-danger' : 'bg-warning'} text-white flex-shrink-0`} style={{ fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}>
                                                {log.status.toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="card-body py-2 px-3">
                                            {/* Address Comparison */}
                                            <div className="row g-2 mb-2">
                                                <div className="col-md-6">
                                                    <div className="border rounded p-2" style={{ backgroundColor: '#f9f9f9' }}>
                                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>ORIGINAL</div>
                                                        <div style={{ fontSize: '0.85rem', lineHeight: '1.3' }}>
                                                            <div>{log.originalStreet1}</div>
                                                            {log.originalStreet2 && <div>{log.originalStreet2}</div>}
                                                            <div>{log.originalCity}, {log.originalState} {log.originalZip}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="border rounded p-2" style={{ backgroundColor: '#f0f8ff' }}>
                                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>CORRECTED</div>
                                                        <div style={{ fontSize: '0.85rem', lineHeight: '1.3' }}>
                                                            <div>{log.geminiCorrectedStreet1 || log.originalStreet1}</div>
                                                            {log.geminiCorrectedStreet2 && <div>{log.geminiCorrectedStreet2}</div>}
                                                            <div>{log.geminiCorrectedCity || log.originalCity}, {log.geminiCorrectedState || log.originalState} {log.geminiCorrectedZip || log.originalZip}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Stats Row */}
                                            <div className="d-flex gap-2 mb-2 flex-wrap">
                                                {log.modelUsed && (
                                                    <div className="border rounded p-2" style={{ fontSize: '0.75rem', flex: '0 1 auto' }}>
                                                        <div className="text-muted" style={{ fontSize: '0.65rem' }}>MODEL</div>
                                                        <div className="fw-medium">{log.modelUsed}</div>
                                                    </div>
                                                )}
                                                {log.confidenceScore !== null && log.confidenceScore !== undefined && (
                                                    <div className="border rounded p-2" style={{ fontSize: '0.75rem', flex: '0 1 auto' }}>
                                                        <div className="text-muted" style={{ fontSize: '0.65rem' }}>CONFIDENCE</div>
                                                        <div className="fw-medium">{`${(log.confidenceScore).toFixed(0)}%`}</div>
                                                    </div>
                                                )}
                                                <div className="border rounded p-2" style={{ fontSize: '0.75rem', flex: '0 1 auto' }}>
                                                    <div className="text-muted" style={{ fontSize: '0.65rem' }}>ACCEPTED</div>
                                                    <span className={`badge ${log.wasCorrectionAccepted ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                                        {log.wasCorrectionAccepted ? 'Yes' : 'No'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Change Type & Status Reason */}
                                            {(log.changeType || log.statusReason) && (
                                                <div className="border rounded p-2 mb-2" style={{ backgroundColor: '#f8f9fa', fontSize: '0.8rem' }}>
                                                    {log.changeType && (
                                                        <div className="mb-2">
                                                            <span className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>CHANGE</span>
                                                            <div className="mt-1">{log.changeType}</div>
                                                            {log.changesDetailed && <div className="text-muted small mt-1">{log.changesDetailed}</div>}
                                                        </div>
                                                    )}
                                                    {log.changeType && log.statusReason && <div className="border-bottom my-2"></div>}
                                                    {log.statusReason && (
                                                        <div>
                                                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>REASON</span>
                                                            <div className="mt-1">{log.statusReason}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Collapsible JSON Sections */}
                                            <div className="mt-2">
                                                {log.geminiRequest && (
                                                    <details className="mb-2">
                                                        <summary style={{ fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500, padding: '0.25rem 0' }}>
                                                            <i className="bi bi-chevron-right" style={{ fontSize: '0.7rem' }}></i> Request
                                                        </summary>
                                                        <div style={{ marginTop: '0.5rem' }}>
                                                            <JsonViewer
                                                                title=""
                                                                data={(() => {
                                                                    try {
                                                                        return JSON.parse(log.geminiRequest);
                                                                    } catch {
                                                                        return log.geminiRequest;
                                                                    }
                                                                })()}
                                                                maxHeight="250px"
                                                            />
                                                        </div>
                                                    </details>
                                                )}

                                                {log.geminiResponse && (
                                                    <details className="mb-2">
                                                        <summary style={{ fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500, padding: '0.25rem 0' }}>
                                                            <i className="bi bi-chevron-right" style={{ fontSize: '0.7rem' }}></i> Response
                                                        </summary>
                                                        <div style={{ marginTop: '0.5rem' }}>
                                                            <JsonViewer
                                                                title=""
                                                                data={(() => {
                                                                    try {
                                                                        return JSON.parse(log.geminiResponse);
                                                                    } catch {
                                                                        return log.geminiResponse;
                                                                    }
                                                                })()}
                                                                maxHeight="250px"
                                                            />
                                                        </div>
                                                    </details>
                                                )}

                                                {log.fieldConfidenceScores && (
                                                    <details>
                                                        <summary style={{ fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500, padding: '0.25rem 0' }}>
                                                            <i className="bi bi-chevron-right" style={{ fontSize: '0.7rem' }}></i> Field Scores
                                                        </summary>
                                                        <div style={{ marginTop: '0.5rem' }}>
                                                            <JsonViewer
                                                                title=""
                                                                data={(() => {
                                                                    try {
                                                                        return JSON.parse(log.fieldConfidenceScores);
                                                                    } catch {
                                                                        return log.fieldConfidenceScores;
                                                                    }
                                                                })()}
                                                                maxHeight="250px"
                                                            />
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Elastic Logs (Active Tab) */}
            {activeTab === 'elastic' && (
                <div>
                    {isLoadingValidationHistory ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading validation history...</p>
                        </div>
                    ) : validationHistory.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-inbox fs-1 text-muted opacity-50"></i>
                            <p className="mt-2 text-muted">No validation history found for this address</p>
                        </div>
                    ) : (
                        <div className="accordion" id="validationHistoryAccordion">
                            {validationHistory.map((item, idx) => (
                                <div className="accordion-item" key={item.id}>
                                    <h2 className="accordion-header" id={`heading-${item.id}`}>
                                        <button
                                            className={`accordion-button${expandedValidationHistoryId === item.id ? '' : ' collapsed'}`}
                                            type="button"
                                            onClick={() => toggleValidationHistoryItem(item.id)}
                                            aria-expanded={expandedValidationHistoryId === item.id}
                                            aria-controls={`collapse-${item.id}`}
                                        >
                                            <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                                                <span>
                                                    <i className="bi bi-clock-history me-2"></i>
                                                    Request ID: {item.id}
                                                </span>
                                                <small className="text-muted">
                                                    {new Date(item.createdAt).toLocaleString()}
                                                </small>
                                            </div>
                                        </button>
                                    </h2>
                                    <div
                                        id={`collapse-${item.id}`}
                                        className={`accordion-collapse collapse${expandedValidationHistoryId === item.id ? ' show' : ''}`}
                                        aria-labelledby={`heading-${item.id}`}
                                        data-bs-parent="#validationHistoryAccordion"
                                    >
                                        <div className="accordion-body">
                                            <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                                <code className="text-dark">
                                                    {JSON.stringify(item, null, 2)}
                                                </code>
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
                </div>
            </div>

            {/* Correction Modals */}
            {traceId && (
                <>
                    <CorrectCoordinatesModal
                        isOpen={showCorrectCoordinates}
                        onClose={() => setShowCorrectCoordinates(false)}
                        traceId={traceId}
                        address={address}
                        correctedBy={userInfo?.email || 'portal-user'}
                        onSuccess={handleCoordinatesCorrected}
                    />
                    <CorrectFullAddressModal
                        isOpen={showCorrectFullAddress}
                        onClose={() => setShowCorrectFullAddress(false)}
                        traceId={traceId}
                        address={address}
                        correctedBy={userInfo?.email || 'portal-user'}
                        onSuccess={handleFullAddressCorrected}
                    />
                </>
            )}

            {/* Refresh Confirmation Modal */}
            {showRefreshConfirm && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Confirm Address Re-evaluation
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowRefreshConfirm(false)}
                                    disabled={isRefreshing}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to re-evaluate this address?</p>
                                <p className="text-muted mb-0">
                                    This will perform a full re-validation of the address using Google and Radar APIs and update all validation data.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowRefreshConfirm(false)}
                                    disabled={isRefreshing}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleRefreshAddress}
                                    disabled={isRefreshing}
                                >
                                    {isRefreshing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Re-evaluating...
                                        </>
                                    ) : (
                                        'Confirm Re-evaluation'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddressDetails;
